import { useCallback } from 'react';

import { LDFlags } from '../../constants/flags';
import {
  SupportedLienholderResponse,
  useAcquirePayoffMutation,
  useCheckSupportedLienholderLazyQuery,
} from '../../gql/generated/graphql';
import useFlag from '../useFlag';

const DEFAULT_LIENHOLDER_SUPPORTED_RESPONSE: Readonly<SupportedLienholderResponse> = {
  isSupported: false,
  requirementsMet: false,
  requiredFields: [],
  missingRequiredFields: [],
};

export const usePayoffIntelligence = () => {
  const payoffIntelligenceEnabled = useFlag(LDFlags.PAYOFF_INTELLIGENCE_ENABLED);
  const payoffIntelligenceStressTestEnabled = useFlag(LDFlags.PAYOFF_INTELLIGENCE_STRESS_TEST);

  const [checkSupportedLienholder] = useCheckSupportedLienholderLazyQuery({
    context: { isErrorHandled: true },
    fetchPolicy: 'network-only',
  });
  const [acquirePayoff] = useAcquirePayoffMutation({ context: { isErrorHandled: true } });

  const checkLienholderSupportedForPI = useCallback(
    async (dealId?: number) => {
      if (!payoffIntelligenceEnabled && !payoffIntelligenceStressTestEnabled) {
        return DEFAULT_LIENHOLDER_SUPPORTED_RESPONSE;
      }

      try {
        const { data } = await checkSupportedLienholder({ variables: { dealId } });
        return data?.checkSupportedLienholder ?? DEFAULT_LIENHOLDER_SUPPORTED_RESPONSE;
      } catch {
        return DEFAULT_LIENHOLDER_SUPPORTED_RESPONSE;
      }
    },
    [payoffIntelligenceEnabled, payoffIntelligenceStressTestEnabled, checkSupportedLienholder],
  );

  const sendPayoffRequest = useCallback(
    async (payloadFields: string[]) => {
      await acquirePayoff({
        variables: {
          payloadFields,
        },
      });
    },
    [acquirePayoff],
  );

  return {
    checkLienholderSupportedForPI,
    sendPayoffRequest,
  };
};
